import { fetchJobCoverContext, fetchJobHardwareContext } from '../api.js';
import { ComboBatteryBanner } from '../components/ComboBatteryBanner.js';
import { JobCoverPanel } from '../components/JobCoverPanel.js';
import { JobHardwarePanel } from '../components/JobHardwarePanel.js';
/**
 * One inspection case — every field, the history timeline, and the lifecycle
 * actions the current status allows (JOB_TRANSITIONS from shared).
 *
 * Assigning opens a small modal (inspector from KNOWN_INSPECTORS + optional
 * scheduled date); cancelling asks for an optional note; every other move is
 * a one-click transition. Illegal hops surface the backend's 409 reason
 * verbatim (the matrix can change server-side without a UI redeploy).
 */

import { useCallback, useEffect, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  addressLineWithGeolocateHint,
  JOB_TRANSITIONS,
  KNOWN_INSPECTORS,
  type JobStatus,
  type JobTransitionRequest,
  type InspectionJob,
} from '@cer-autofill/shared';
import {
  apiErrorReason,
  fetchCase,
  fetchClients,
  openJobSignedConsentPdf,
  saveBooking,
  transitionCase,
} from '../api.js';
import { getSession } from '../session.js';
import {
  jobStatusLabel,
  jobStatusPillClass,
  formatDateOnly,
  formatDateTime,
  inspectionTypeBadgeClass,
  inspectionTypeLabel,
} from '../format.js';

/** Button label per transition target, tweaked for context where it reads better. */
function transitionLabel(from: JobStatus, to: JobStatus): string {
  if (to === 'confirmed' && from === 'assigned') return 'Unassign';
  if (to === 'needs-confirmation' && from === 'confirmed') return 'Back to needs confirmation';
  if (to === 'assigned' && from === 'in-field') return 'Back to assigned';
  if (to === 'in-field' && from === 'submitted') return 'Send back to field';
  if (to === 'submitted' && (from === 'qa-passed' || from === 'approved-for-upload')) {
    return 'Revoke (back to submitted)';
  }
  switch (to) {
    case 'needs-confirmation':
      return 'Needs confirmation';
    case 'confirmed':
      return 'Confirm';
    case 'assigned':
      return 'Assign inspector…';
    case 'in-field':
      return 'Mark in field';
    case 'submitted':
      return 'Mark inspection completed';
    case 'qa-passed':
      return 'Mark QA passed';
    case 'approved-for-upload':
      return 'Approve for upload';
    case 'uploaded':
      return 'Mark submitted to CER';
    case 'closed':
      return 'Mark invoiced';
    case 'cancelled':
      return 'Reject job…';
    default:
      return jobStatusLabel(to);
  }
}

export function JobDetailScreen() {
  const params = useParams<{ jobId: string }>();
  const jobId = params.jobId ?? '';

  const [data, setData] = useState<InspectionJob | null>(null);
  const [conflictCount, setConflictCount] = useState(0);
  const [clientName, setClientName] = useState('');
  const [loadError, setLoadError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [cancelOpen, setCancelOpen] = useState(false);

  const load = useCallback(() => {
    setLoadError(null);
    void Promise.all([
      fetchCase(jobId),
      fetchClients(),
      fetchJobHardwareContext(jobId),
      fetchJobCoverContext(jobId),
    ])
      .then(([job, clients, hardwareContext, coverContext]) => {
        setConflictCount(
          hardwareContext.unresolvedConflictCount + coverContext.unresolvedConflicts.length,
        );
        setData(job);
        setClientName(
          clients.find((client) => client.clientId === job.clientId)?.name ?? job.clientId,
        );
      })
      .catch((e: unknown) => setLoadError(apiErrorReason(e)));
  }, [jobId]);

  useEffect(() => {
    load();
  }, [load]);

  async function doTransition(body: JobTransitionRequest) {
    setBusy(true);
    setActionError(null);
    try {
      const next = await transitionCase(jobId, body);
      setData(next);
      setAssignOpen(false);
      setCancelOpen(false);
    } catch (e) {
      setActionError(apiErrorReason(e));
    } finally {
      setBusy(false);
    }
  }

  async function doSaveBooking(inspectorId: string, scheduledDate: string) {
    setBusy(true);
    setActionError(null);
    try {
      const next = await saveBooking(jobId, { inspectorId, scheduledDate });
      setData(next);
      setAssignOpen(false);
    } catch (e) {
      setActionError(apiErrorReason(e));
    } finally {
      setBusy(false);
    }
  }

  if (loadError) {
    return (
      <div className="screen">
        <p>
          <Link to="/jobs" className="back-link">
            ← Back to jobs
          </Link>
        </p>
        <p className="error">{loadError}</p>
        <button type="button" onClick={load}>
          Retry
        </button>
      </div>
    );
  }

  if (!data) {
    return (
      <div className="screen">
        <p className="loading">Loading job…</p>
      </div>
    );
  }

  const roles = getSession()?.roles ?? [];
  const isAdmin = roles.includes('admin');
  const isSupport = roles.includes('support');
  const isInspectorOnly =
    roles.includes('inspector') && !roles.some((role) => role !== 'inspector');
  const supportTargets: JobStatus[] = [
    'needs-confirmation',
    'confirmed',
    'assigned',
    'in-field',
    'cancelled',
  ];
  const canEditBooking =
    (isAdmin || isSupport || isInspectorOnly) &&
    (data.status === 'assigned' || JOB_TRANSITIONS[data.status].includes('assigned'));
  const allowed = JOB_TRANSITIONS[data.status].filter(
    (target) =>
      target !== 'assigned' && (isAdmin || (isSupport && supportTargets.includes(target))),
  );
  const hasSubmittedReport = [
    'submitted',
    'qa-passed',
    'approved-for-upload',
    'uploaded',
    'closed',
  ].includes(data.status);

  return (
    <div className="screen">
      <div className="screen__header">
        <Link to="/jobs" className="back-link">
          ← Back
        </Link>
        <div>
          <h2>
            {data.jobId}{' '}
            <span className={inspectionTypeBadgeClass(data.inspectionType)}>
              {inspectionTypeLabel(data.inspectionType)}
            </span>{' '}
            <span className={jobStatusPillClass(data.status)}>{jobStatusLabel(data.status)}</span>
          </h2>
          <div className="subtitle">
            {data.owner.firstName} {data.owner.lastName} · {data.address.suburb} · updated{' '}
            {formatDateTime(data.updatedAt)}
          </div>
        </div>
      </div>

      <ComboBatteryBanner job={data} />

      <div className="action-bar">
        <div className="action-bar__current">
          <span>Current status:</span>
          <span className={jobStatusPillClass(data.status)}>{jobStatusLabel(data.status)}</span>
        </div>
        {canEditBooking && (
          <button type="button" disabled={busy} onClick={() => setAssignOpen(true)}>
            {data.scheduledDate ? 'Edit booking…' : 'Create booking…'}
          </button>
        )}
        {!canEditBooking && allowed.length === 0 && (
          <span className="muted">No actions available for your role.</span>
        )}
        {allowed.map((to) => (
          <button
            key={to}
            type="button"
            className={
              to === 'cancelled'
                ? 'danger'
                : to === 'confirmed' && data.status !== 'assigned'
                  ? 'success'
                  : ''
            }
            disabled={
              busy || (conflictCount > 0 && ['qa-passed', 'approved-for-upload'].includes(to))
            }
            title={
              conflictCount > 0 && ['qa-passed', 'approved-for-upload'].includes(to)
                ? `Resolve ${conflictCount} conflict(s) on the latest report before QA passed or approval.`
                : undefined
            }
            onClick={() => {
              if (to === 'assigned') setAssignOpen(true);
              else if (to === 'cancelled') setCancelOpen(true);
              else void doTransition({ to });
            }}
          >
            {transitionLabel(data.status, to)}
          </button>
        ))}
        {hasSubmittedReport && (
          <button
            type="button"
            disabled={busy}
            onClick={() => {
              setBusy(true);
              setActionError(null);
              void openJobSignedConsentPdf(jobId)
                .catch((error) => setActionError(apiErrorReason(error)))
                .finally(() => setBusy(false));
            }}
          >
            View signed consent
          </button>
        )}
      </div>

      {conflictCount > 0 && (
        <p role="status">
          Resolve {conflictCount} conflict(s) on the latest report before marking QA passed or
          approving for upload.
        </p>
      )}
      {actionError && <p className="error">Transition failed: {actionError}</p>}

      {(isAdmin || roles.includes('qa')) && (
        <>
          <JobCoverPanel key={`${data.jobId}-cover`} job={data} onSaved={setData} />
          <JobHardwarePanel key={data.jobId} job={data} onSaved={setData} />
        </>
      )}

      <div className="detail-grid">
        {/* Case fields ────────────────────────────────────────────────── */}
        <div className="card">
          <h3>Job</h3>
          <dl>
            <Row label="Job ID" value={data.jobId} />
            <Row label="Inspection type" value={data.inspectionType} />
            <Row label="Related accreditation code" value={data.relatedCaseId ?? '—'} />
            <Row label="Client" value={clientName || data.clientId} />
            <Row label="Client ref" value={data.sourceRef ?? '—'} />
            <Row label="ServiceM8 job #" value={data.serviceM8JobNumber ?? '—'} />
            <Row label="System type" value={data.systemType ?? '—'} />
            <Row label="Battery installation" value={data.batteryInstallationType ?? '—'} />
            <Row label="Installation date" value={formatDateOnly(data.installationDate)} />
            <Row label="Pre-confirmed" value={data.preConfirmed ? 'Yes' : 'No'} />
            <Row label="Inspector" value={data.inspectorId ?? '—'} />
            <Row label="Scheduled date" value={formatDateOnly(data.scheduledDate)} />
            <Row label="Created" value={formatDateTime(data.createdAt)} />
            <Row label="Submitted" value={formatDateTime(data.submittedAt)} />
            <Row label="Approved for portal" value={formatDateTime(data.approvedAt)} />
            <Row label="Updated" value={formatDateTime(data.updatedAt)} />
          </dl>
        </div>

        {/* Site + owner ───────────────────────────────────────────────── */}
        <div className="card">
          <h3>Site &amp; owner</h3>
          <dl>
            <Row
              label="Address"
              value={addressLineWithGeolocateHint(data.address, data.location?.matchedAddress)}
            />
            <Row label="Suburb" value={data.address.suburb} />
            <Row label="State" value={data.address.state} />
            <Row label="Postcode" value={data.address.postcode} />
            <Row label="Owner" value={`${data.owner.firstName} ${data.owner.lastName}`.trim()} />
            <Row label="Email" value={data.owner.email ?? '—'} />
            <Row label="Phone" value={data.owner.phone ?? '—'} />
          </dl>
        </div>

        {/* History ────────────────────────────────────────────────────── */}
        <div className="card">
          <h3>History</h3>
          {data.history.length === 0 && <p className="muted">No events recorded.</p>}
          {data.history.length > 0 && (
            <ul className="audit-list">
              {[...data.history].reverse().map((event, i) => (
                <li key={i} className="audit-entry">
                  <div className="audit-entry__when">
                    {formatDateTime(event.at)} · {event.actor}
                  </div>
                  <div className="audit-entry__transition">
                    <span className={jobStatusPillClass(event.from)}>
                      {jobStatusLabel(event.from)}
                    </span>{' '}
                    →{' '}
                    <span className={jobStatusPillClass(event.to)}>{jobStatusLabel(event.to)}</span>
                  </div>
                  {event.note && <div className="audit-entry__note">{event.note}</div>}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      {assignOpen && (
        <AssignModal
          busy={busy}
          currentInspectorId={data.inspectorId}
          currentScheduledDate={data.scheduledDate}
          lockInspector={isInspectorOnly}
          onCancel={() => setAssignOpen(false)}
          onSubmit={(inspectorId, scheduledDate) => void doSaveBooking(inspectorId, scheduledDate)}
        />
      )}

      {cancelOpen && (
        <CancelModal
          busy={busy}
          jobId={data.jobId}
          onCancel={() => setCancelOpen(false)}
          onSubmit={(note) => void doTransition({ to: 'cancelled', ...(note ? { note } : {}) })}
        />
      )}
    </div>
  );
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div className="card__row">
      <dt>{label}</dt>
      <dd>{value}</dd>
    </div>
  );
}

function AssignModal({
  busy,
  currentInspectorId,
  currentScheduledDate,
  lockInspector,
  onCancel,
  onSubmit,
}: {
  busy: boolean;
  currentInspectorId: string | undefined;
  currentScheduledDate: string | undefined;
  lockInspector: boolean;
  onCancel: () => void;
  onSubmit: (inspectorId: string, scheduledDate: string) => void;
}) {
  const [inspectorId, setInspectorId] = useState(currentInspectorId ?? '');
  const [scheduledDate, setScheduledDate] = useState(currentScheduledDate ?? '');
  return (
    <div className="modal" onClick={onCancel}>
      <div className="modal__card" onClick={(e) => e.stopPropagation()}>
        <h3>{currentScheduledDate ? 'Edit booking' : 'Create booking'}</h3>
        <p className="subtitle">
          Scheduling stays in ServiceM8 — the date here is a cross-reference only.
        </p>
        <label>
          <span>Inspector</span>
          <select
            autoFocus
            value={inspectorId}
            disabled={lockInspector}
            onChange={(e) => setInspectorId(e.target.value)}
          >
            <option value="">Select inspector…</option>
            {KNOWN_INSPECTORS.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </select>
        </label>
        <label style={{ marginTop: 12 }}>
          <span>Scheduled date</span>
          <input
            type="date"
            value={scheduledDate}
            onChange={(e) => setScheduledDate(e.target.value)}
          />
        </label>
        <div className="modal__actions">
          <button type="button" onClick={onCancel} disabled={busy}>
            Cancel
          </button>
          <button
            type="button"
            className="primary"
            disabled={busy || !inspectorId || !scheduledDate}
            onClick={() => onSubmit(inspectorId, scheduledDate)}
          >
            {busy ? 'Saving…' : 'Save booking'}
          </button>
        </div>
      </div>
    </div>
  );
}

function CancelModal({
  busy,
  jobId,
  onCancel,
  onSubmit,
}: {
  busy: boolean;
  jobId: string;
  onCancel: () => void;
  onSubmit: (note: string) => void;
}) {
  const [note, setNote] = useState('');
  return (
    <div className="modal" onClick={onCancel}>
      <div className="modal__card" onClick={(e) => e.stopPropagation()}>
        <h3>Cancel job {jobId}</h3>
        <p className="subtitle">
          Cancelled is terminal — the job leaves the pipeline. Add a note for the history trail
          (optional).
        </p>
        <label>
          <span>Note</span>
          <textarea
            autoFocus
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="e.g. Homeowner withdrew; client pulled the job from the batch."
          />
        </label>
        <div className="modal__actions">
          <button type="button" onClick={onCancel} disabled={busy}>
            Keep job
          </button>
          <button
            type="button"
            className="danger"
            disabled={busy}
            onClick={() => onSubmit(note.trim())}
          >
            {busy ? 'Cancelling…' : 'Cancel job'}
          </button>
        </div>
      </div>
    </div>
  );
}
